// Utility functions for reuse across the project
import dayjs from 'dayjs';
import _ from 'lodash';
import { FilterSelector } from '../Constants';
import { FilterSelectorProps } from '../stories/Components/CustomDropdowns/AgFilterSelector';
import { AxiosError } from 'axios';

export function capitalizeFirstLetter(string = '') {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const formatDate = (date?: string | null, format = 0) => {
  if(!date) return '';
  const formattedDate = new Date(date); 
  const day = String(formattedDate.getDate()).padStart(2, '0');
  const month = String(formattedDate.getMonth() + 1).padStart(2, '0');
  const year = formattedDate.getFullYear();
  if(format === 0) return `${day}-${month}-${year}`;
  if(format === 1) return `${year}-${month}-${day}`;
};

export const formatDateTime = (date?: string | null, format?: string) => {
  if (!date) return '';
  return dayjs(date).format(format || 'DD-MM-YYYY HH:mm');
};

// Converts an simple array of objects to a record using specified keys for the object and value.
export const mapToKeyValue = (items: Record<string, any>[], { objectKey = 'id', valueKey = 'name' }: { objectKey?: string, valueKey?: string } = {}): Record<string, any> => {
  return (items || []).reduce((acc, item) => ({ ...acc, [item[objectKey]]: item[valueKey] }), {});
};

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(2)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
};

// Joins the first name, middle name, and last name of a item.
export const joinName = (formData: {
  firstName?: string | null;
  middleName?: string | null;
  familyName?: string | null;
}) => {
  return [
    formData?.firstName || '',
    formData?.middleName || '',
    formData?.familyName || '',
  ]
    .filter(Boolean)
    .join(' ');
};

export const filterData = (data: any[], filterSelector: FilterSelectorProps[]) => {
  let filteredData = _.cloneDeep(data);
   filterSelector.forEach((filter) => {
    const filterKey = filter.field?.name || '';
    filteredData = filteredData.filter((member) => {
      switch (filter.condition) {
        case FilterSelector.Is:
          // allowing searching for a number as well
          // eslint-disable-next-line eqeqeq
          return member[filterKey] == filter.truncation;
      case FilterSelector.IsWithIcon:
        return dayjs(member[filterKey]).format('DD/MM/YYYY') === dayjs(filter.truncation, 'DD/MM/YYYY').format('DD/MM/YYYY');
      case FilterSelector.EqualToWithIcon:
        return (
          Number(member[filterKey]) === Number(filter.truncation)
        );
      case FilterSelector.IsNot:
        return member[filterKey] !== filter.truncation;
      case FilterSelector.NotEqualToWithIcon:
        return (
          Number(member[filterKey]) !== Number(filter.truncation)
        );
      case FilterSelector.IsEmpty:
        return !member[filterKey];
      case FilterSelector.IsNotEmpty:
        return member[filterKey];
      case FilterSelector.Contains:
        return (member[filterKey] || '').toString()
          ?.toUpperCase()
          .includes(filter.truncation.toUpperCase()); // not case-sensitive
      case FilterSelector.DoesNotContain:
        return !member[filterKey]
          ?.toUpperCase()
          .includes(filter.truncation.toUpperCase()); // not case-sensitive
      case FilterSelector.StartsWith:
        return member[filterKey]
          ?.toUpperCase()
          .startsWith(filter.truncation.toUpperCase()); // not case-sensitive
      case FilterSelector.EndsWith:
        return member[filterKey]
          ?.toUpperCase()
          .endsWith(filter.truncation.toUpperCase()); // not case-sensitive
      case FilterSelector.GreaterThanWithIcon:
        return Number(member[filterKey]) > Number(filter.truncation);
      case FilterSelector.LessThanWithIcon:
        return Number(member[filterKey]) < Number(filter.truncation);
      case FilterSelector.GreaterThanOrEqualWithIcon:
        return Number(member[filterKey]) >= Number(filter.truncation);
      case FilterSelector.LessThanOrEqualToWithIcon:
        return Number(member[filterKey]) <= Number(filter.truncation);
      case FilterSelector.IsOnOrAfterWithIcon:
        return dayjs(member[filterKey]).format('DD/MM/YYYY') >= dayjs(filter.truncation, 'DD/MM/YYYY').format('DD/MM/YYYY');
      case FilterSelector.IsOnOrBeforeWithIcon:
        return dayjs(member[filterKey]).format('DD/MM/YYYY') <= dayjs(filter.truncation, 'DD/MM/YYYY').format('DD/MM/YYYY');
      default:
        return member;
    }
    });
  });
  
  return filteredData;
};

/**
 * Removes all falsy properties from an object or array.
 * @template T - The type of the object or array.
 * @param obj - The object or array to remove falsy properties from.
 * @returns The object or array with all falsy properties removed.
 */
export const removeFalsyProperties = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map(removeFalsyProperties).filter(Boolean) as unknown as T;
  }

  if (typeof obj === 'object' && obj !== null) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value && typeof value === 'object') {
        const cleaned = removeFalsyProperties(value);
        if (Object.keys(cleaned).length > 0) acc[key] = cleaned;
      } else if (value) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
  }

  return obj;
};

/**
 * Adds three years to the current date.
 * @returns The date three years from now in ISO format.
 */
export const addThreeYearsFromToday = (): string => {
  const now = new Date();
  now.setFullYear(now.getFullYear() + 3);
  return now.toISOString();
};
export const timeout = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms ?? 3000));

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&+*!=]).{8,}$/;
export const validatePassword = (password: string) => {
  return passwordRegex.test(password);
};

export const getClientError = (response: AxiosError<any>) => {
  const error = (response.response?.data?.errors || [])?.[0];
  const clientMessage = error?.isClientError ? error?.detail : '';
  return clientMessage;
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const base64 = reader.result as string;
      resolve(base64);
    };

    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

export const parseJSON = (jsonString: string = '') => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    try {
      const fixedString = jsonString
        .replace(/,(\s*[}\]])/g, '$1')
        .replace(/([{,]\s*)([a-zA-Z0-9_]+?)\s*:/g, '$1"$2":')
        .replace(/'([^']*)'/g, '"$1"');
      return JSON.parse(fixedString);
    } catch (e) {
      return null;
    }
  }
};
